/**
 * Utility functions for any storefront wizard region
 * TO-DO: refactor StorefrontWizardRegion and possibly CentralizedWizardRegion to make use of this utils file
 */

import * as React from "react";
import { ExtendedFTNode } from "src/typings/flexFramework/FlexComponent";
import {
  BlossomComponentFactory,
  BlossomComponentFactoryProps,
} from "src/components/flexFramework/domain/BlossomComponentFactory";
import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { WizardModuleDataOptions } from "src/components/flexComponents/WizardPWARegions/StorefrontWizardRegionMeso/typings";
import { WizardBackgroundImageOverrideProps } from "src/components/flexComponents/WizardPWARegions/typings";
import { FlexModuleModelStore } from "src/stores/FlexModuleModelStore";
import { normalizeOptionsData } from "src/components/flexFramework/FlexTemplateNode";
import { WizardComponentFTNode, WizardComponentFTNodeOptions } from "./typings";
import { ExtendedTemplateComponent } from "src/stores/ExperienceTemplateStore/typings";

/**
 *
 * @param flexModules Array of flex modules coming from the flex manager
 * Returns all modules corresponding to a wizard LOB form
 */

export const getWizardLOBFlexComponents = (flexModules: ExtendedFTNode[]): ExtendedFTNode[] => {
  return flexModules.filter((module: ExtendedFTNode) => {
    return module && module.id && module.id.includes("wizard");
  });
};

/**
 *
 * @param flexModules Array of flex modules coming from the flex manager
 * Returns all modules corresponding to a wizard LOB form
 */

export const getWizardLOBTemplateComponents = (
  flexModules: ExtendedTemplateComponent[]
): ExtendedTemplateComponent[] => {
  return flexModules.filter((module: ExtendedTemplateComponent) => {
    return module?.metadata?.id?.includes("wizard");
  });
};

/**
 *
 * @param children Array of react components output by BlossomComponent
 * Returns all react components corresponding to a wizard LOB form
 */
export const getWizardLOBFormChildren = (children: React.ReactElement[]): React.ReactElement[] => {
  return children.filter((child) => {
    const { key } = child;

    return key && key.toString().includes("wizard") && !key.toString().includes("biased");
  });
};

/**
 *
 * @param flexModules Array of flex modules coming from the flex manager
 * Returns all modules corresponding to a wizard LOB form
 */
export const getBiasedWizardLOBFlexComponent = (flexModules: ExtendedFTNode[]): ExtendedFTNode[] => {
  return flexModules.filter((module: ExtendedFTNode) => {
    return module?.id?.includes("biased");
  });
};

/**
 *
 * @param flexModules Array of flex modules coming from the flex manager
 * Returns all modules corresponding to a wizard LOB form
 */
export const getBiasedWizardLOBTemplateComponent = (
  flexModules: ExtendedTemplateComponent[]
): ExtendedTemplateComponent[] => {
  return flexModules.filter((module: ExtendedTemplateComponent) => {
    return module?.metadata?.id?.includes("biased");
  });
};

/**
 *
 * @param children Array of react components output by BlossomComponent
 * Returns all react components that do not correspond to a wizard LOB form
 * Does not categorize react components further
 */
export const getNonWizardFormChildren = (
  children: React.ReactElement[],
  flexModuleModelStore: FlexModuleModelStore
): React.ReactElement[] => {
  return children
    .filter((child) => {
      const { key } = child;

      return key && !key.toString().includes("wizard");
    })
    .map((child) => mapNonWizardFormChildren(child, flexModuleModelStore));
};

const mapNonWizardFormChildren = (
  child: React.ReactElement,
  flexModuleModelStore: FlexModuleModelStore
): React.ReactElement => {
  const { key } = child;

  const childComponent = BlossomComponentFactory.get(child.props as BlossomComponentFactoryProps);
  const flexTemplateNode = childComponent?.flexTemplateNode as WizardComponentFTNode;
  const childOptions = normalizeOptionsData(
    flexTemplateNode,
    flexModuleModelStore.getModel(flexTemplateNode.id)
  ) as WizardComponentFTNodeOptions;
  if (childOptions && childOptions.horizontalCenter === false) {
    return (
      <div key={`parent-${key}`} className="prevent-justify-center">
        {child}
      </div>
    );
  }

  return <div key={`parent-${key}`}>{child}</div>;
};

/**
 *
 * @param children Array of react components output by BlossomComponent
 * Returns all react components that do not correspond to a wizard LOB form and they're before to the wizard
 * Does not categorize react components further
 */
export const getAboveNonWizardFormChildren = (
  children: React.ReactElement[],
  flexModuleModelStore: FlexModuleModelStore
): React.ReactElement[] => {
  const firstIndexWizardChildren = children.findIndex(({ key }) => {
    return key && key.toString().includes("wizard");
  });

  //splice to get the elements before the first wizard element
  return children
    .splice(0, firstIndexWizardChildren)
    .filter((child) => {
      const { key } = child;

      return key && !key.toString().includes("wizard");
    })
    .map((child) => mapNonWizardFormChildren(child, flexModuleModelStore));
};

/**
 *
 * @param children Array of react components output by BlossomComponent
 * Returns all react components that do not correspond to a wizard LOB form and they're next to the wizard
 * Does not categorize react components further
 */
export const getBelowNonWizardFormChildren = (
  children: React.ReactElement[],
  flexModuleModelStore: FlexModuleModelStore
): React.ReactElement[] => {
  const firstIndexWizardChildren = children.findIndex(({ key }) => {
    return key && key.toString().includes("wizard");
  });

  return children
    .filter((child, childIndex) => {
      const { key } = child;

      return key && !key.toString().includes("wizard") && childIndex > firstIndexWizardChildren;
    })
    .map((child) => mapNonWizardFormChildren(child, flexModuleModelStore));
};

export const getWizardImageOverride = (
  children: React.ReactElement[],
  context: ExtendedContextStore,
  flexModulesModelStore: FlexModuleModelStore
): WizardBackgroundImageOverrideProps | undefined => {
  const {
    locale,
    site: { id: siteid },
  } = context;
  let backgroundImage;
  let backgroundImageLocale;
  children.forEach((child) => {
    const { key } = child;
    if (key && key.toString().includes("background-image-override")) {
      const childComponent = BlossomComponentFactory.get(child.props as BlossomComponentFactoryProps);
      const flexTemplateNode = childComponent?.flexTemplateNode as WizardComponentFTNode;
      const options = normalizeOptionsData(
        flexTemplateNode,
        flexModulesModelStore.getModel(flexTemplateNode.id)
      ) as WizardComponentFTNodeOptions;
      const childSiteid = options?.siteid;
      const childLocale = options?.locale;
      const matchSiteId = childSiteid === siteid;
      const backgroundObject = {
        src: options?.backgroundUrl,
        color: options?.backgroundColor,
      };
      if (matchSiteId && !childLocale) {
        backgroundImage = backgroundObject;
      } else if (matchSiteId && childLocale === locale) {
        backgroundImageLocale = backgroundObject;
      }
    }
  });
  return backgroundImageLocale ?? backgroundImage;
};

/**
 * Based on a list of ReactElement, will filter and return
 * the ones that are part of Packages as standalone LOBs
 * i.e. Hotels, Flights and Cars
 */
export const getStandaloneLOBModulesInPackages = (children: React.ReactElement[]) => {
  const standaloneLobsInPackages = ["wizard-hotel-pwa-v2", "wizard-flight-pwa", "wizard-car-pwa"];

  return children.filter((lobForm: React.ReactElement) => {
    const { name } = lobForm.props.flexComponent;

    return standaloneLobsInPackages.includes(name);
  });
};

/**
 * This function overrides the configs for the biased wizard
 * Which takes flexcomponent and wizardState as input.
 * Used in StoreFrontWizardRegionMeso
 */
export const getMesoWizardModuleData = (options: WizardModuleDataOptions, wizardState: ClientWizardStateStore) => {
  const globalConfig = wizardState.globalWizardState.config;
  globalConfig.halfWizard = options.halfWizard || false;
  globalConfig.selectedProduct = options.selectedProduct;
  globalConfig.defaultOrigin = options.biasingOriginAttributeValue;

  globalConfig.defaultMultipleDestination =
    options.biasingDestinationAttributeMap !== undefined ? options.biasingDestinationAttributeMap[""] : undefined;
  globalConfig.defaultDestination = options.biasingDestinationAttributeValue;
  globalConfig.defaultStopover = options.biasingStopoverAttributeValue;
  globalConfig.defaultMultipleStopover = options.biasingStopoverAttributeMap
    ? options.biasingStopoverAttributeMap[""]
    : undefined;

  globalConfig.defaultMultipleOrigin =
    options.biasingOriginAttributeMap !== undefined ? options.biasingOriginAttributeMap[""] : undefined;
  globalConfig.destinationDropdownTypeahead = options.destDropdownTypeahead;
  globalConfig.calendarFilterCarrierCode = options.calendarFilterCarrierCode;
  globalConfig.displayBiasedWizard = !options.empty;
  globalConfig.userLocation = options.airportName;
  globalConfig.destiantionType = options.destinationBiased;
  globalConfig.originType = options.originBiased;

  const hotelConfig = wizardState.hotelWizardState.config;
  wizardState.hotelWizardState.overrideConfig(() => {
    hotelConfig.hotelID = options.biasingAttributeHotelValue;
    hotelConfig.hotelName = options.biasingAttributeHotelName;
    hotelConfig.hotelChainID = options.biasingAttributeHotelChainID;
    hotelConfig.multiHotelIDs =
      (options.biasingAttributeMultiHotelValue && options.biasingAttributeMultiHotelValue.split(";")) || undefined;
    hotelConfig.isBiasedWizardEmpty = options.empty;
  });

  const carConfig = wizardState.carWizardState.config;
  wizardState.carWizardState.overrideConfig(() => {
    carConfig.carType = options.biasingAttributeCarType;
    carConfig.carVendorCode = options.biasingAttributeCarVendorCode;
    carConfig.carVendorName = options.biasingAttributeCarVendorName;
    carConfig.carDiscountTypeCode = options.carDiscountTypeCode;
    carConfig.carDiscountTypeName = options.carDiscountTypeName;
    carConfig.carDiscountCode = options.carDiscountCode;
    carConfig.showCarDiscount = options.showCarDiscount;
    carConfig.isBiasedWizardEmpty = options.empty;
  });

  const cruiseConfig = wizardState.cruiseWizardState.config;
  wizardState.cruiseWizardState.overrideConfig(() => {
    cruiseConfig.cruiseLine = options.biasingAttributeCruiseLine;
  });

  const flightConfig = wizardState.flightWizardState.config;
  wizardState.flightWizardState.overrideConfig(() => {
    flightConfig.airlineOptions = options.airlineOptions;
    flightConfig.cabinClass = options.biasedCabinClass;
    if (!!globalConfig.defaultStopover || !!globalConfig.defaultMultipleStopover) {
      flightConfig.multiCityFlight.maxNumberOfAdditionalLegs = 1;
      flightConfig.multiCityFlight.minNumberOfLegs = 1;

      const getOriginValue = () => {
        if (globalConfig.defaultStopover) {
          return {
            stopoverName: globalConfig.defaultStopover,
            stopoverRegionId: "",
          };
        }
        if (globalConfig.defaultMultipleStopover && globalConfig.defaultMultipleStopover.length > 0) {
          const value = globalConfig.defaultMultipleStopover[0].split(":");

          return {
            stopoverName: value[0],
            stopoverRegionId: value[1],
          };
        }

        return {
          stopoverName: "",
          stopoverRegionId: "",
        };
      };

      const { stopoverName, stopoverRegionId } = getOriginValue();

      wizardState.flightWizardState.listOfAdditionalLegs.push({
        location: {
          origin: {
            value: stopoverName,
            metaData: {
              regionId: stopoverRegionId,
              ttla: "",
              latLong: "",
              countryCode: "",
            },
          },
          destination: {
            value: "",
            metaData: {
              regionId: "",
              ttla: "",
              latLong: "",
              countryCode: "",
            },
          },
        },
        date: wizardState.globalWizardState.getDateFromConfig(wizardState.flightWizardState.config.date),
      });
    }
  });

  const packageConfig = wizardState.packageWizardState.config;
  wizardState.packageWizardState.overrideConfig(() => {
    packageConfig.multiHotelIDs =
      (options.biasingAttributeMultiHotelValue && options.biasingAttributeMultiHotelValue.split(";")) || undefined;
  });
};
